import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
  className?: string;
  toolbar?: string;
  plugins?: string[];
  menubar?: boolean;
  statusbar?: boolean;
  branding?: boolean;
  elementpath?: boolean;
  resize?: boolean | 'both' | 'vertical';
  content_style?: string;
}

const defaultPlugins = [
  'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
  'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
  'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons',
  'codesample', 'quickbars'
];

const defaultToolbar = 
  'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | ' +
  'bullist numlist outdent indent | removeformat | link image media table | ' +
  'forecolor backcolor | emoticons charmap | preview code fullscreen | help';

const defaultContentStyle = `
  body { 
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; 
    font-size: 14px; 
    line-height: 1.6;
    color: #374151;
    margin: 1rem;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.375rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }
  blockquote {
    border-left: 4px solid #e5e7eb;
    margin: 1.5rem 0;
    padding-left: 1rem;
    font-style: italic;
    color: #6b7280;
  }
  pre {
    background-color: #f3f4f6;
    border-radius: 0.375rem;
    padding: 1rem;
    overflow-x: auto;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
  }
  table th, table td {
    border: 1px solid #e5e7eb;
    padding: 0.75rem;
    text-align: left;
  }
  table th {
    background-color: #f9fafb;
    font-weight: 600;
  }
  a {
    color: #2563eb;
    text-decoration: underline;
  }
  a:hover {
    color: #1d4ed8;
  }
`;

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder = 'Start typing...',
  height = 400,
  disabled = false,
  className,
  toolbar = defaultToolbar,
  plugins = defaultPlugins,
  menubar = false,
  statusbar = true,
  branding = false,
  elementpath = false,
  resize = 'vertical',
  content_style = defaultContentStyle,
}) => {
  const editorRef = useRef<any>(null);

  const handleEditorChange = (content: string) => {
    if (onChange) {
      onChange(content);
    }
  };

  const editorInit = {
    height,
    menubar,
    plugins,
    toolbar,
    statusbar,
    branding,
    elementpath,
    resize,
    content_style,
    placeholder,
    
    // Content filtering and cleanup
    paste_data_images: true,
    paste_as_text: false,
    paste_retain_style_properties: 'color font-size font-family background-color',
    paste_remove_styles_if_webkit: false,
    
    // Image handling
    automatic_uploads: true,
    file_picker_types: 'image',
    images_upload_handler: async (blobInfo: any) => {
      // TODO: Implement Supabase Storage upload
      // For now, return a data URL
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blobInfo.blob());
      });
    },
    
    // Link handling
    link_default_target: '_blank',
    link_assume_external_targets: true,
    
    // Table settings
    table_default_attributes: {
      border: '1'
    },
    table_default_styles: {
      'border-collapse': 'collapse'
    },
    
    // Advanced settings
    contextmenu: 'link image table',
    quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
    quickbars_insert_toolbar: 'quickimage quicktable',
    
    // Mobile responsive
    mobile: {
      theme: 'silver',
      plugins: ['autosave', 'lists', 'autolink'],
      toolbar: 'undo redo | bold italic | bullist numlist | link'
    },
    
    // Accessibility
    a11y_advanced_options: true,
    
    // Custom styles for better integration
    skin: 'oxide',
    
    // Performance
    browser_spellcheck: true,
    contextmenu_never_use_native: true,
    
    setup: (editor: any) => {
      editor.on('init', () => {
        if (onFocus) {
          editor.on('focus', onFocus);
        }
        if (onBlur) {
          editor.on('blur', onBlur);
        }
      });
      
      // Custom button for asset library (placeholder)
      editor.ui.registry.addButton('assetlibrary', {
        text: 'Assets',
        tooltip: 'Insert from Asset Library',
        onAction: () => {
          // TODO: Open asset library modal
          console.log('Asset library would open here');
        }
      });
      
      // Add custom format for callout boxes
      editor.formatter.register('callout', {
        block: 'div',
        classes: 'callout',
        wrapper: true
      });
      
      editor.ui.registry.addToggleButton('callout', {
        text: 'Callout',
        tooltip: 'Create callout box',
        onAction: () => {
          editor.formatter.toggle('callout');
        },
        onSetup: (api: any) => {
          const nodeChangeHandler = () => {
            api.setActive(editor.formatter.match('callout'));
          };
          editor.on('NodeChange', nodeChangeHandler);
          return () => editor.off('NodeChange', nodeChangeHandler);
        }
      });
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <Editor
        ref={editorRef}
        value={value}
        onEditorChange={handleEditorChange}
        disabled={disabled}
        init={editorInit}
        apiKey="no-api-key" // For self-hosted version
      />
      
      {/* Custom styles for callout boxes */}
      <style>{`
        .tox .tox-edit-area {
          border-radius: 0.375rem;
        }
        .callout {
          background-color: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 1rem;
          margin: 1rem 0;
          border-radius: 0.375rem;
        }
        .callout p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;